import { useMemo, useState, useRef, useCallback } from 'react';
import { Search, Edit2, Trash2 } from 'lucide-react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { motion } from 'framer-motion';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { useSchedulingContext } from '../../pages/schedules/SchedulingCanvas';
import { useAgents } from '../../hooks/useAgents';
import { useScheduleIntervals, ScheduleInterval, useUpdateScheduleInterval, useBulkCreateScheduleIntervals, useDeleteScheduleIntervals } from '../../hooks/useScheduleIntervals';
import { validateInterval } from '../../utils/scheduleConstraints';

export default function AgentTimeline() {
  const { filterCriteria, setFilterCriteria, cursorPosition, scheduleId, selectedDate, blockPainterActive, painterActivityType, showToast, selectedAgents, setSelectedAgents } = useSchedulingContext();
  const { data: agents, isLoading: agentsLoading } = useAgents();

  // Fetch schedule intervals for all agents on the selected date
  const { data: scheduleIntervals, isLoading: intervalsLoading } = useScheduleIntervals({
    scheduleId: scheduleId || undefined,
    date: selectedDate,
  });

  const isLoading = agentsLoading || intervalsLoading;

  // Generate demo data if no intervals exist
  const intervalsWithDemo = useMemo(() => {
    if (scheduleIntervals && scheduleIntervals.length > 0) {
      console.log('Using real schedule intervals from database');
      return scheduleIntervals;
    }

    if (!agents || agents.length === 0 || !scheduleId) {
      console.log('No agents or scheduleId available for demo data');
      return [];
    }

    console.log('Generating demo schedule intervals for date:', selectedDate.toISOString().split('T')[0]);

    // Generate demo intervals for first 30 agents with varied activities
    const demoIntervals: ScheduleInterval[] = [];
    const dateStr = selectedDate.toISOString().split('T')[0];

    agents.slice(0, 30).forEach((agent, index) => {
      // Create 8-hour shifts with staggered start times and varied activities
      const startHour = 9 + (index % 3) * 2; // 9am, 11am, or 1pm start times
      let currentInterval = startHour * 4; // Convert to 15-min intervals (9am = 36)
      let intervalIdCounter = 0;

      // Morning session (2 hours) - Use channel as activityType for proper coloring
      const morningChannel = index % 3 === 0 ? 'PHONE' : index % 3 === 1 ? 'CHAT' : 'EMAIL';
      demoIntervals.push({
        id: `demo-interval-${agent.id}-${dateStr}-${intervalIdCounter++}`,
        scheduleId: scheduleId,
        agentId: agent.id,
        date: dateStr,
        intervalStart: currentInterval,
        intervalEnd: currentInterval + 8,
        activityType: morningChannel,
        channel: morningChannel,
        isAIGenerated: false,
        isOptimized: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        agent: {
          id: agent.id,
          firstName: agent.firstName,
          lastName: agent.lastName,
          email: agent.email,
          employeeId: agent.employeeId,
        },
      });
      currentInterval += 8;

      // Morning break (15 min)
      demoIntervals.push({
        id: `demo-interval-${agent.id}-${dateStr}-${intervalIdCounter++}`,
        scheduleId: scheduleId,
        agentId: agent.id,
        date: dateStr,
        intervalStart: currentInterval,
        intervalEnd: currentInterval + 1,
        activityType: 'BREAK',
        isAIGenerated: false,
        isOptimized: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        agent: {
          id: agent.id,
          firstName: agent.firstName,
          lastName: agent.lastName,
          email: agent.email,
          employeeId: agent.employeeId,
        },
      });
      currentInterval += 1;

      // Mid-morning session (1.75 hours)
      const midMorningChannel = index % 3 === 0 ? 'CHAT' : index % 3 === 1 ? 'EMAIL' : 'PHONE';
      demoIntervals.push({
        id: `demo-interval-${agent.id}-${dateStr}-${intervalIdCounter++}`,
        scheduleId: scheduleId,
        agentId: agent.id,
        date: dateStr,
        intervalStart: currentInterval,
        intervalEnd: currentInterval + 7,
        activityType: midMorningChannel,
        channel: midMorningChannel,
        isAIGenerated: false,
        isOptimized: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        agent: {
          id: agent.id,
          firstName: agent.firstName,
          lastName: agent.lastName,
          email: agent.email,
          employeeId: agent.employeeId,
        },
      });
      currentInterval += 7;

      // Lunch break (30 min) - Use MEAL for proper color (amber)
      demoIntervals.push({
        id: `demo-interval-${agent.id}-${dateStr}-${intervalIdCounter++}`,
        scheduleId: scheduleId,
        agentId: agent.id,
        date: dateStr,
        intervalStart: currentInterval,
        intervalEnd: currentInterval + 2,
        activityType: 'MEAL',
        isAIGenerated: false,
        isOptimized: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        agent: {
          id: agent.id,
          firstName: agent.firstName,
          lastName: agent.lastName,
          email: agent.email,
          employeeId: agent.employeeId,
        },
      });
      currentInterval += 2;

      // Afternoon session (2 hours)
      const afternoonChannel = index % 3 === 0 ? 'EMAIL' : index % 3 === 1 ? 'PHONE' : 'CHAT';
      demoIntervals.push({
        id: `demo-interval-${agent.id}-${dateStr}-${intervalIdCounter++}`,
        scheduleId: scheduleId,
        agentId: agent.id,
        date: dateStr,
        intervalStart: currentInterval,
        intervalEnd: currentInterval + 8,
        activityType: afternoonChannel,
        channel: afternoonChannel,
        isAIGenerated: false,
        isOptimized: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        agent: {
          id: agent.id,
          firstName: agent.firstName,
          lastName: agent.lastName,
          email: agent.email,
          employeeId: agent.employeeId,
        },
      });
      currentInterval += 8;

      // Afternoon break (15 min)
      demoIntervals.push({
        id: `demo-interval-${agent.id}-${dateStr}-${intervalIdCounter++}`,
        scheduleId: scheduleId,
        agentId: agent.id,
        date: dateStr,
        intervalStart: currentInterval,
        intervalEnd: currentInterval + 1,
        activityType: 'BREAK',
        isAIGenerated: false,
        isOptimized: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        agent: {
          id: agent.id,
          firstName: agent.firstName,
          lastName: agent.lastName,
          email: agent.email,
          employeeId: agent.employeeId,
        },
      });
      currentInterval += 1;

      // Final session (1.75 hours)
      const finalChannel = index % 3 === 0 ? 'PHONE' : index % 3 === 1 ? 'CHAT' : 'EMAIL';
      demoIntervals.push({
        id: `demo-interval-${agent.id}-${dateStr}-${intervalIdCounter++}`,
        scheduleId: scheduleId,
        agentId: agent.id,
        date: dateStr,
        intervalStart: currentInterval,
        intervalEnd: currentInterval + 7,
        activityType: finalChannel,
        channel: finalChannel,
        isAIGenerated: false,
        isOptimized: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        agent: {
          id: agent.id,
          firstName: agent.firstName,
          lastName: agent.lastName,
          email: agent.email,
          employeeId: agent.employeeId,
        },
      });
    });

    console.log(`Generated ${demoIntervals.length} demo intervals`);
    return demoIntervals;
  }, [scheduleIntervals, agents, scheduleId, selectedDate]);

  // Debug logging
  console.log('AgentTimeline Debug:', {
    scheduleId,
    selectedDate: selectedDate.toISOString().split('T')[0],
    intervalsCount: intervalsWithDemo?.length || 0,
    agentsCount: agents?.length || 0,
    isLoading,
    sampleInterval: intervalsWithDemo?.[0] // Show first interval for debugging
  });

  // Drag and drop state
  const [activeBlock, setActiveBlock] = useState<any>(null);
  const updateInterval = useUpdateScheduleInterval();
  const bulkCreateIntervals = useBulkCreateScheduleIntervals();
  const deleteIntervals = useDeleteScheduleIntervals();

  // Agent detail modal state
  const [selectedAgentForDetail, setSelectedAgentForDetail] = useState<any | null>(null);

  // Scroll synchronization refs
  const agentListRef = useRef<VirtuosoHandle>(null);
  const timelineCanvasRef = useRef<VirtuosoHandle>(null);

  // Sync scroll from agent list to timeline
  const handleAgentListScroll = useCallback((top: number) => {
    timelineCanvasRef.current?.scrollTo({ top });
  }, []);

  // Sync scroll from timeline to agent list
  const handleTimelineScroll = useCallback((top: number) => {
    agentListRef.current?.scrollTo({ top });
  }, []);

  // Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement to start drag
      },
    })
  );

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveBlock(event.active.data.current);
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || !active.data.current) {
      setActiveBlock(null);
      return;
    }

    // Get the dropped block data
    const block = active.data.current as any;
    const overData = over.data.current as any;

    // Calculate new interval based on drop position
    if (overData?.type === 'timeline-row') {
      const newAgentId = overData.agentId;
      const dropX = event.delta.x;
      const intervalWidth = 20; // 20px per 15-min interval (compressed)

      // Calculate interval shift based on drag distance
      const intervalShift = Math.round(dropX / intervalWidth);
      const newIntervalStart = Math.max(0, Math.min(95, block.intervalStart + intervalShift));
      const newIntervalEnd = Math.max(0, Math.min(95, block.intervalEnd + intervalShift));

      // Validate the new interval position
      const violations = validateInterval(
        {
          id: block.id, // Pass the ID so we don't check against ourselves
          agentId: newAgentId,
          intervalStart: newIntervalStart,
          intervalEnd: newIntervalEnd,
        },
        intervalsWithDemo || []
      );

      // Show errors and prevent if there are error-level violations
      const errors = violations.filter((v) => v.severity === 'error');
      if (errors.length > 0) {
        errors.forEach((err) => showToast(err.message, 'error'));
        setActiveBlock(null);
        return;
      }

      // Show warnings but allow operation
      const warnings = violations.filter((v) => v.severity === 'warning');
      warnings.forEach((warn) => showToast(warn.message, 'warning'));

      // Update the interval
      updateInterval.mutate({
        id: block.id,
        updates: {
          agentId: newAgentId,
          intervalStart: newIntervalStart,
          intervalEnd: newIntervalEnd,
        },
      });
    }

    setActiveBlock(null);
  };

  // Filter agents based on criteria
  const filteredAgents = useMemo(() => {
    if (!agents) return [];

    const filtered = agents.filter((agent) => {
      // Search filter
      if (filterCriteria.search) {
        const searchLower = filterCriteria.search.toLowerCase();
        const fullName = `${agent.firstName} ${agent.lastName}`.toLowerCase();
        const email = agent.email.toLowerCase();
        if (!fullName.includes(searchLower) && !email.includes(searchLower)) return false;
      }

      // Status filter
      if (filterCriteria.statuses && filterCriteria.statuses.length > 0) {
        if (!filterCriteria.statuses.includes(agent.status)) return false;
      }

      // Channel filter - check if agent has any intervals with the selected channels
      if (filterCriteria.channels && filterCriteria.channels.length > 0) {
        const agentIntervals = intervalsWithDemo?.filter((interval) => interval.agentId === agent.id) || [];
        const agentChannels = agentIntervals
          .map((interval) => interval.channel)
          .filter((channel): channel is string => channel !== null && channel !== undefined);

        // Agent must have at least one interval with one of the selected channels
        const hasMatchingChannel = filterCriteria.channels.some((selectedChannel) =>
          agentChannels.includes(selectedChannel)
        );

        if (!hasMatchingChannel) return false;
      }

      // Skill filter - check if agent has the selected skills
      // Note: This requires agent.skills to be populated from the API
      if (filterCriteria.skills && filterCriteria.skills.length > 0) {
        if (!agent.skills || agent.skills.length === 0) return false;

        const agentSkillIds = agent.skills.map((skill: any) => skill.skillGroupId || skill.id);
        const hasMatchingSkill = filterCriteria.skills.some((selectedSkill) =>
          agentSkillIds.includes(selectedSkill)
        );

        if (!hasMatchingSkill) return false;
      }

      // Team filter (TODO: add team data to agent model)

      return true;
    });

    // Sort agents: scheduled agents first, then by name
    return filtered.sort((a, b) => {
      const aIntervals = intervalsWithDemo?.filter((interval) => interval.agentId === a.id) || [];
      const bIntervals = intervalsWithDemo?.filter((interval) => interval.agentId === b.id) || [];

      // Agents with schedules come first
      if (aIntervals.length > 0 && bIntervals.length === 0) return -1;
      if (aIntervals.length === 0 && bIntervals.length > 0) return 1;

      // If both have schedules or both don't, sort by name
      const aName = `${a.firstName} ${a.lastName}`.toLowerCase();
      const bName = `${b.firstName} ${b.lastName}`.toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [agents, filterCriteria, intervalsWithDemo]);

  // Bulk action handlers
  const handleSelectAll = () => {
    setSelectedAgents(filteredAgents.map((agent) => agent.id));
  };

  const handleDeselectAll = () => {
    setSelectedAgents([]);
  };

  const handleToggleAgent = (agentId: string) => {
    if (selectedAgents.includes(agentId)) {
      setSelectedAgents(selectedAgents.filter((id: string) => id !== agentId));
    } else {
      setSelectedAgents([...selectedAgents, agentId]);
    }
  };

  const handleBulkDeleteIntervals = () => {
    if (!intervalsWithDemo || selectedAgents.length === 0) return;

    const intervalIds = intervalsWithDemo
      .filter((interval) => selectedAgents.includes(interval.agentId))
      .map((interval) => interval.id);

    if (intervalIds.length === 0) {
      showToast('No intervals found for selected agents', 'warning');
      return;
    }

    if (confirm(`Delete all ${intervalIds.length} intervals for ${selectedAgents.length} selected agent(s)?`)) {
      deleteIntervals.mutate(intervalIds, {
        onSuccess: () => {
          showToast(`Successfully deleted ${intervalIds.length} intervals`, 'success');
          setSelectedAgents([]);
        },
        onError: () => {
          showToast('Failed to delete intervals', 'error');
        },
      });
    }
  };

  const handleBulkAssignActivity = (activityType: 'PHONE' | 'CHAT' | 'EMAIL' | 'MEAL' | 'BREAK' | 'COACHING' | 'TRAINING' | 'MEETING') => {
    if (!scheduleId || selectedAgents.length === 0) return;

    // Create full-day intervals (0-95) for each selected agent
    const newIntervals = selectedAgents.map((agentId) => ({
      scheduleId,
      agentId,
      date: selectedDate.toISOString().split('T')[0],
      intervalStart: 0,
      intervalEnd: 95,
      activityType,
      channel: ['PHONE', 'CHAT', 'EMAIL'].includes(activityType) ? activityType : undefined,
      isAIGenerated: false,
      isOptimized: false,
    }));

    if (confirm(`Assign ${activityType} for full day to ${selectedAgents.length} agent(s)?`)) {
      bulkCreateIntervals.mutate(newIntervals, {
        onSuccess: () => {
          showToast(`Successfully assigned ${activityType} to ${selectedAgents.length} agents`, 'success');
          setSelectedAgents([]);
        },
        onError: () => {
          showToast('Failed to assign activity', 'error');
        },
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading agents...</div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col h-full bg-white">
        {/* Top Toolbar: Search, Filters, Activity Legend */}
        <div className="border-b border-gray-200 px-4 py-2.5 flex items-center gap-4 flex-shrink-0">
          {/* Search */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search agents..."
              value={filterCriteria.search}
              onChange={(e) =>
                setFilterCriteria({ ...filterCriteria, search: e.target.value })
              }
              className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Filters */}
          <AgentFilters
            filterCriteria={filterCriteria}
            setFilterCriteria={setFilterCriteria}
          />

          {/* Activity Legend - Clickable with Horizontal Scroll */}
          <div className="flex items-center gap-2 ml-auto max-w-2xl">
            <span className="text-xs font-semibold text-gray-700 flex-shrink-0">Filter by Activity:</span>
            <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pb-1">
              {(['PHONE', 'CHAT', 'EMAIL', 'MEAL', 'BREAK', 'MEETING', 'TRAINING', 'COACHING'] as const).map((activity) => {
                const colors: Record<string, string> = {
                  PHONE: '#8b5cf6',
                  CHAT: '#06b6d4',
                  EMAIL: '#6366f1',
                  MEAL: '#f59e0b',
                  BREAK: '#fb923c',
                  MEETING: '#3b82f6',
                  COACHING: '#ec4899',
                  TRAINING: '#8b5cf6',
                };
                // Check if this activity is in the filter
                const isActive = filterCriteria.channels?.includes(activity) || false;

                return (
                  <button
                    key={activity}
                    onClick={() => {
                      const currentChannels = filterCriteria.channels || [];
                      const newChannels = currentChannels.includes(activity)
                        ? currentChannels.filter((c: string) => c !== activity)
                        : [...currentChannels, activity];
                      setFilterCriteria({ ...filterCriteria, channels: newChannels });
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs transition-all flex-shrink-0 ${
                      isActive ? 'ring-2 ring-blue-500 shadow-sm bg-blue-50' : 'hover:bg-gray-100'
                    }`}
                    title={`Filter by ${activity}`}
                  >
                    <div className="w-3 h-3 rounded flex-shrink-0" style={{ backgroundColor: colors[activity] }} />
                    <span className="text-gray-700 font-medium whitespace-nowrap">{activity}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Column: Agent List Only */}
          <div className="w-64 flex-shrink-0 border-r border-gray-200 flex flex-col">

          {/* Bulk Actions Toolbar */}
          {selectedAgents.length > 0 && (
            <BulkActionsToolbar
              selectedCount={selectedAgents.length}
              onDeselectAll={handleDeselectAll}
              onBulkDelete={handleBulkDeleteIntervals}
              onBulkAssign={handleBulkAssignActivity}
            />
          )}

          {/* Select All / Deselect All */}
          {filteredAgents.length > 0 && (
            <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
              <button
                onClick={selectedAgents.length === filteredAgents.length ? handleDeselectAll : handleSelectAll}
                className="text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                {selectedAgents.length === filteredAgents.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          )}

          {/* Agent List (Virtualized) */}
          <div className="flex-1 overflow-hidden">
            <Virtuoso
              ref={agentListRef}
              data={filteredAgents}
              itemContent={(_index, agent) => (
                <AgentListItem
                  key={agent.id}
                  agent={agent}
                  isSelected={selectedAgents.includes(agent.id)}
                  onToggleSelect={() => handleToggleAgent(agent.id)}
                  onAgentClick={() => setSelectedAgentForDetail(agent)}
                />
              )}
              rangeChanged={(range) => {
                // Sync scroll position to timeline when agent list scrolls
                if (range.startIndex >= 0) {
                  const scrollTop = range.startIndex * 72; // 72px row height
                  handleAgentListScroll(scrollTop);
                }
              }}
              className="h-full"
            />
          </div>

          {/* Summary */}
          <div className="p-3 border-t border-gray-200 text-xs text-gray-600">
            {selectedAgents.length > 0 && (
              <div className="mb-1 text-blue-600 font-medium">
                {selectedAgents.length} selected
              </div>
            )}
            Showing {filteredAgents.length} of {agents?.length || 0} agents
          </div>
          </div>

          {/* Right Column: Timeline Canvas (horizontally scrollable) */}
          <div className="flex-1 overflow-x-auto overflow-y-hidden">
            <TimelineCanvas
              agents={filteredAgents}
              cursorPosition={cursorPosition}
              intervalsWithDemo={intervalsWithDemo || []}
              blockPainterActive={blockPainterActive}
              painterActivityType={painterActivityType}
              bulkCreateIntervals={bulkCreateIntervals}
              scheduleId={scheduleId}
              selectedDate={selectedDate}
              showToast={showToast}
              virtuosoRef={timelineCanvasRef}
              onScroll={handleTimelineScroll}
            />
          </div>
        </div>
      </div>

      {/* Drag Overlay - shows block while dragging */}
      <DragOverlay>
        {activeBlock ? (
          <div
            className="rounded px-2 py-1 text-xs font-medium text-white shadow-lg opacity-80"
            style={{
              width: activeBlock.width,
              height: 40,
              backgroundColor: activeBlock.color,
            }}
          >
            <div className="flex items-center justify-between h-full">
              <span className="truncate">{activeBlock.activityType}</span>
            </div>
          </div>
        ) : null}
      </DragOverlay>

      {/* Agent Detail Modal */}
      {selectedAgentForDetail && (
        <AgentDetailModal
          agent={selectedAgentForDetail}
          onClose={() => setSelectedAgentForDetail(null)}
        />
      )}
    </DndContext>
  );
}

// Agent Filters Component
interface FilterCriteria {
  search: string;
  teams: string[];
  skills: string[];
  channels: string[];
  statuses: string[];
  availability?: {
    hour: number;
    channel: string;
  };
}

interface AgentFiltersProps {
  filterCriteria: FilterCriteria;
  setFilterCriteria: (criteria: FilterCriteria) => void;
}

function AgentFilters({ filterCriteria, setFilterCriteria }: AgentFiltersProps) {
  const [showFilters, setShowFilters] = useState(false);

  // Status filter options
  const statusOptions = [
    { value: 'ACTIVE', label: 'Active', color: 'bg-green-100 text-green-800' },
    { value: 'INACTIVE', label: 'Inactive', color: 'bg-gray-100 text-gray-800' },
    { value: 'ON_LEAVE', label: 'On Leave', color: 'bg-yellow-100 text-yellow-800' },
  ];

  // Channel filter options
  const channelOptions = [
    { value: 'PHONE', label: 'Phone', color: 'bg-purple-100 text-purple-800' },
    { value: 'CHAT', label: 'Chat', color: 'bg-teal-100 text-teal-800' },
    { value: 'EMAIL', label: 'Email', color: 'bg-gray-100 text-gray-800' },
    { value: 'SOCIAL', label: 'Social', color: 'bg-blue-100 text-blue-800' },
    { value: 'SMS', label: 'SMS', color: 'bg-indigo-100 text-indigo-800' },
  ];

  // Skill filter options (removed redundant channel duplicates)
  const skillOptions = [
    { value: 'TECHNICAL', label: 'Technical', color: 'bg-amber-100 text-amber-800' },
    { value: 'BILLING', label: 'Billing', color: 'bg-emerald-100 text-emerald-800' },
  ];

  const toggleStatus = (status: string) => {
    const currentStatuses = filterCriteria.statuses || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter((s: string) => s !== status)
      : [...currentStatuses, status];

    setFilterCriteria({ ...filterCriteria, statuses: newStatuses });
  };

  const toggleChannel = (channel: string) => {
    const currentChannels = filterCriteria.channels || [];
    const newChannels = currentChannels.includes(channel)
      ? currentChannels.filter((c: string) => c !== channel)
      : [...currentChannels, channel];

    setFilterCriteria({ ...filterCriteria, channels: newChannels });
  };

  const toggleSkill = (skill: string) => {
    const currentSkills = filterCriteria.skills || [];
    const newSkills = currentSkills.includes(skill)
      ? currentSkills.filter((s: string) => s !== skill)
      : [...currentSkills, skill];

    setFilterCriteria({ ...filterCriteria, skills: newSkills });
  };

  const hasActiveFilters =
    (filterCriteria.statuses?.length > 0) ||
    (filterCriteria.channels?.length > 0) ||
    (filterCriteria.skills?.length > 0);

  const totalActiveFilters =
    (filterCriteria.statuses?.length || 0) +
    (filterCriteria.channels?.length || 0) +
    (filterCriteria.skills?.length || 0);

  return (
    <div className="relative">
      {/* Filter Toggle Button */}
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
      >
        <span className="flex items-center">
          Filters
          {hasActiveFilters && (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-500 text-white rounded-full">
              {totalActiveFilters}
            </span>
          )}
        </span>
        <svg
          className={`h-4 w-4 transition-transform ${showFilters ? 'transform rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Filter Options - Floating Dropdown */}
      {showFilters && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          <div className="space-y-3 p-4">
          {/* Status Filter */}
          <div>
            <label className="text-xs font-medium text-gray-700 mb-2 block">Status</label>
            <div className="space-y-1.5">
              {statusOptions.map((option) => {
                const isSelected = filterCriteria.statuses?.includes(option.value) || false;
                return (
                  <button
                    key={option.value}
                    onClick={() => toggleStatus(option.value)}
                    className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded transition-colors ${
                      isSelected
                        ? option.color + ' font-medium'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span>{option.label}</span>
                    {isSelected && (
                      <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Channel Filter */}
          <div>
            <label className="text-xs font-medium text-gray-700 mb-2 block">Channel</label>
            <div className="space-y-1.5">
              {channelOptions.map((option) => {
                const isSelected = filterCriteria.channels?.includes(option.value) || false;
                return (
                  <button
                    key={option.value}
                    onClick={() => toggleChannel(option.value)}
                    className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded transition-colors ${
                      isSelected
                        ? option.color + ' font-medium'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span>{option.label}</span>
                    {isSelected && (
                      <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Skill Filter */}
          <div>
            <label className="text-xs font-medium text-gray-700 mb-2 block">Skill</label>
            <div className="space-y-1.5">
              {skillOptions.map((option) => {
                const isSelected = filterCriteria.skills?.includes(option.value) || false;
                return (
                  <button
                    key={option.value}
                    onClick={() => toggleSkill(option.value)}
                    className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded transition-colors ${
                      isSelected
                        ? option.color + ' font-medium'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span>{option.label}</span>
                    {isSelected && (
                      <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={() =>
                setFilterCriteria({
                  ...filterCriteria,
                  statuses: [],
                  channels: [],
                  skills: [],
                })
              }
              className="w-full px-2 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded hover:bg-red-50 transition-colors"
            >
              Clear All Filters
            </button>
          )}
          </div>
        </div>
      )}
    </div>
  );
}

// Bulk Actions Toolbar Component
type ActivityType = 'PHONE' | 'CHAT' | 'EMAIL' | 'MEAL' | 'BREAK' | 'COACHING' | 'TRAINING' | 'MEETING';

interface BulkActionsToolbarProps {
  selectedCount: number;
  onDeselectAll: () => void;
  onBulkDelete: () => void;
  onBulkAssign: (activity: ActivityType) => void;
}

function BulkActionsToolbar({ selectedCount, onDeselectAll, onBulkDelete, onBulkAssign }: BulkActionsToolbarProps) {
  const [showAssignMenu, setShowAssignMenu] = useState(false);

  return (
    <div className="px-4 py-3 bg-blue-50 border-b border-blue-200">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-blue-900">
          {selectedCount} agent{selectedCount !== 1 ? 's' : ''} selected
        </div>
        <button
          onClick={onDeselectAll}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
        >
          Clear
        </button>
      </div>

      <div className="flex gap-2">
        {/* Bulk Delete */}
        <button
          onClick={onBulkDelete}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-300 rounded hover:bg-red-50 transition-colors"
        >
          <Trash2 className="h-3 w-3" />
          Clear Schedules
        </button>

        {/* Bulk Assign */}
        <div className="relative">
          <button
            onClick={() => setShowAssignMenu(!showAssignMenu)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-50 transition-colors"
          >
            <Edit2 className="h-3 w-3" />
            Assign Activity
          </button>

          {showAssignMenu && (
            <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
              <div className="py-1">
                {(['PHONE', 'CHAT', 'EMAIL', 'TRAINING', 'COACHING', 'MEETING'] as ActivityType[]).map((activity) => (
                  <button
                    key={activity}
                    onClick={() => {
                      onBulkAssign(activity);
                      setShowAssignMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-xs hover:bg-gray-100 transition-colors"
                  >
                    {activity}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Agent List Item Component
interface AgentListItemProps {
  agent: any; // TODO: Use proper Agent type
  isSelected: boolean;
  onToggleSelect: () => void;
  onAgentClick: () => void;
}

function AgentListItem({ agent, isSelected, onToggleSelect, onAgentClick }: AgentListItemProps) {
  // Status badge color mapping
  const statusColors = {
    ACTIVE: 'bg-green-100 text-green-800',
    INACTIVE: 'bg-gray-100 text-gray-800',
    ON_LEAVE: 'bg-yellow-100 text-yellow-800',
    TERMINATED: 'bg-red-100 text-red-800',
  };

  return (
    <div
      className={`flex items-center px-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
        isSelected ? 'bg-blue-50' : ''
      }`}
      style={{ height: 72 }}
      onClick={onAgentClick}
    >
      {/* Checkbox */}
      <div className="flex-shrink-0 mr-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
      </div>

      {/* Avatar */}
      <div className="flex-shrink-0 mr-3">
        <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
          {agent.firstName[0]}
          {agent.lastName[0]}
        </div>
      </div>

      {/* Agent Info */}
      <div className="flex-1 min-w-0">
        <div className="text-base font-semibold text-gray-900 truncate">
          {agent.firstName} {agent.lastName}
        </div>
        <div className="flex items-center space-x-2 mt-1">
          <div className="text-sm text-gray-600 truncate">{agent.email}</div>
          {agent.status && (
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                statusColors[agent.status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'
              }`}
            >
              {agent.status}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Timeline Canvas Component
interface TimelineCanvasProps {
  agents: any[];
  cursorPosition: number | null;
  intervalsWithDemo: ScheduleInterval[] | undefined;
  blockPainterActive: boolean;
  painterActivityType: string | null;
  bulkCreateIntervals: any;
  scheduleId: string | null;
  selectedDate: Date;
  showToast: (message: string, type: 'error' | 'warning' | 'success' | 'info') => void;
  virtuosoRef: React.RefObject<VirtuosoHandle>;
  onScroll: (top: number) => void;
}

function TimelineCanvas({ agents, cursorPosition, intervalsWithDemo, blockPainterActive, painterActivityType, bulkCreateIntervals, scheduleId, selectedDate, showToast, virtuosoRef, onScroll }: TimelineCanvasProps) {
  // Generate 24-hour timeline (00:00 to 23:45 in 15-minute intervals)
  const timeIntervals = useMemo(() => {
    const intervals = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        intervals.push({
          hour,
          minute,
          label: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
        });
      }
    }
    return intervals;
  }, []);

  // Each interval is 20px wide (for 15-minute blocks) - compressed for better UX
  const intervalWidth = 20;
  const totalWidth = timeIntervals.length * intervalWidth; // 96 intervals = 1,920px

  return (
    <div className="h-full flex flex-col">
      {/* Time Axis Header */}
      <div className="flex-shrink-0 sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="flex" style={{ width: totalWidth }}>
          {timeIntervals.map((interval, index) => (
            <div
              key={index}
              className="flex-shrink-0 border-r border-gray-100 text-center py-2"
              style={{ width: intervalWidth }}
            >
              {interval.minute === 0 && interval.hour % 2 === 0 && (
                <span className="text-xs font-semibold text-gray-800">
                  {interval.label}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Timeline Rows Container - Relative positioning for cursor and blocks */}
      <div className="flex-1 relative" style={{ minWidth: totalWidth }}>
        {/* Vertical Cursor Sync Line */}
        {cursorPosition !== null && (
          <div
            className="absolute top-0 bottom-0 w-px bg-blue-400 z-20 pointer-events-none"
            style={{
              left: cursorPosition * 4 * intervalWidth, // 4 intervals per hour
            }}
          />
        )}

        {/* Agent Timeline Rows (Virtualized) */}
        <Virtuoso
          ref={virtuosoRef}
          data={agents}
          itemContent={(_index, agent) => (
            <AgentTimelineRow
              key={agent.id}
              agent={agent}
              totalWidth={totalWidth}
              intervalWidth={intervalWidth}
              timeIntervals={timeIntervals}
              intervalsWithDemo={intervalsWithDemo}
              blockPainterActive={blockPainterActive}
              painterActivityType={painterActivityType}
              bulkCreateIntervals={bulkCreateIntervals}
              scheduleId={scheduleId}
              selectedDate={selectedDate}
              showToast={showToast}
            />
          )}
          rangeChanged={(range) => {
            // Sync scroll position to agent list when timeline scrolls
            if (range.startIndex >= 0) {
              const scrollTop = range.startIndex * 72; // 72px row height
              onScroll(scrollTop);
            }
          }}
          style={{ height: '100%' }}
        />
      </div>
    </div>
  );
}

// Color mapping for activity types - High contrast, modern palette
const ACTIVITY_COLORS: Record<string, string> = {
  PHONE: '#8b5cf6', // vivid purple - primary work activity
  CHAT: '#06b6d4', // bright cyan - secondary work activity
  EMAIL: '#6366f1', // indigo - tertiary work activity
  MEAL: '#f59e0b', // vibrant amber - meal break
  BREAK: '#fb923c', // orange - short break
  COACHING: '#ec4899', // pink - development
  TRAINING: '#8b5cf6', // violet - learning
  MEETING: '#3b82f6', // blue - collaboration
  PTO: '#10b981', // emerald - time off
  SICK: '#ef4444', // red - sick leave
  UNSCHEDULED: '#e5e7eb', // light gray - unassigned
};

// Agent Timeline Row Component
interface AgentTimelineRowProps {
  agent: any;
  totalWidth: number;
  intervalWidth: number;
  timeIntervals: any[];
  intervalsWithDemo: ScheduleInterval[] | undefined;
  blockPainterActive: boolean;
  painterActivityType: string | null;
  bulkCreateIntervals: any;
  scheduleId: string | null;
  selectedDate: Date;
  showToast: (message: string, type: 'error' | 'warning' | 'success' | 'info') => void;
}

function AgentTimelineRow({
  agent,
  totalWidth,
  intervalWidth,
  timeIntervals,
  intervalsWithDemo,
  blockPainterActive,
  painterActivityType,
  bulkCreateIntervals,
  scheduleId,
  selectedDate,
  showToast,
}: AgentTimelineRowProps) {
  const [isPainting, setIsPainting] = useState(false);
  const [paintStart, setPaintStart] = useState<number | null>(null);
  const [paintEnd, setPaintEnd] = useState<number | null>(null);

  // Make row droppable
  const { setNodeRef, isOver } = useDroppable({
    id: `agent-row-${agent.id}`,
    data: {
      type: 'timeline-row',
      agentId: agent.id,
    },
  });

  // Paint mode handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!blockPainterActive || !painterActivityType) return;

    // Calculate which interval was clicked
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const intervalIndex = Math.floor(clickX / intervalWidth);

    setIsPainting(true);
    setPaintStart(intervalIndex);
    setPaintEnd(intervalIndex);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isPainting || !blockPainterActive || !painterActivityType) return;

    // Calculate current interval
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const intervalIndex = Math.floor(mouseX / intervalWidth);

    setPaintEnd(intervalIndex);
  };

  const handleMouseUp = () => {
    if (!isPainting || paintStart === null || paintEnd === null || !painterActivityType || !scheduleId) {
      setIsPainting(false);
      return;
    }

    // Calculate interval range (ensure start < end)
    const start = Math.min(paintStart, paintEnd);
    const end = Math.max(paintStart, paintEnd);

    // Validate the new interval before creating
    const violations = validateInterval(
      {
        agentId: agent.id,
        intervalStart: start,
        intervalEnd: end,
      },
      intervalsWithDemo || []
    );

    // Show errors and prevent if there are error-level violations
    const errors = violations.filter((v) => v.severity === 'error');
    if (errors.length > 0) {
      errors.forEach((err) => showToast(err.message, 'error'));
      setIsPainting(false);
      setPaintStart(null);
      setPaintEnd(null);
      return;
    }

    // Show warnings but allow operation
    const warnings = violations.filter((v) => v.severity === 'warning');
    warnings.forEach((warn) => showToast(warn.message, 'warning'));

    // Create schedule interval via bulk create API
    bulkCreateIntervals.mutate([
      {
        scheduleId,
        agentId: agent.id,
        date: selectedDate.toISOString().split('T')[0],
        intervalStart: start,
        intervalEnd: end,
        activityType: painterActivityType,
        channel: ['PHONE', 'CHAT', 'EMAIL'].includes(painterActivityType) ? painterActivityType : undefined,
        isAIGenerated: false,
        isOptimized: false,
      },
    ]);

    // Reset paint state
    setIsPainting(false);
    setPaintStart(null);
    setPaintEnd(null);
  };

  // Filter intervals for this agent and calculate block positions
  const scheduleBlocks = useMemo(() => {
    if (!intervalsWithDemo) return [];
    const agentIntervals = intervalsWithDemo.filter((interval) => interval.agentId === agent.id);

    // Debug: Log for first few agents to see if intervals are being found
    if (agent.id && agentIntervals.length > 0) {
      console.log(`Agent ${agent.firstName} ${agent.lastName} has ${agentIntervals.length} intervals:`, agentIntervals[0]);
    }

    return agentIntervals.map((interval) => {
      // Calculate position based on intervalStart (0-95)
      const startPosition = interval.intervalStart * intervalWidth;
      // Calculate width based on duration (intervalEnd - intervalStart)
      const duration = interval.intervalEnd - interval.intervalStart + 1;
      const width = duration * intervalWidth;

      return {
        id: interval.id,
        intervalId: interval.id,
        intervalStart: interval.intervalStart,
        intervalEnd: interval.intervalEnd,
        activityType: interval.activityType,
        channel: interval.channel,
        startPosition,
        width,
        color: ACTIVITY_COLORS[interval.activityType] || '#9ca3af',
        startTime: `${Math.floor(interval.intervalStart / 4).toString().padStart(2, '0')}:${((interval.intervalStart % 4) * 15).toString().padStart(2, '0')}`,
        endTime: `${Math.floor((interval.intervalEnd + 1) / 4).toString().padStart(2, '0')}:${(((interval.intervalEnd + 1) % 4) * 15).toString().padStart(2, '0')}`,
        isAIGenerated: interval.isAIGenerated,
        isOptimized: interval.isOptimized,
      };
    });
  }, [agent.id, intervalsWithDemo, intervalWidth]);

  // Calculate paint preview area
  const paintPreview = useMemo(() => {
    if (!isPainting || paintStart === null || paintEnd === null || !painterActivityType) {
      return null;
    }

    const start = Math.min(paintStart, paintEnd);
    const end = Math.max(paintStart, paintEnd);
    const startPosition = start * intervalWidth;
    const width = (end - start + 1) * intervalWidth;
    const color = ACTIVITY_COLORS[painterActivityType] || '#9ca3af';

    return { startPosition, width, color };
  }, [isPainting, paintStart, paintEnd, painterActivityType, intervalWidth]);

  return (
    <div
      ref={setNodeRef}
      className={`relative border-b border-gray-100 transition-colors ${
        isOver ? 'bg-blue-50' : blockPainterActive ? 'hover:bg-yellow-50 cursor-crosshair' : 'hover:bg-gray-50'
      }`}
      style={{ height: 72, width: totalWidth }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => {
        if (isPainting) {
          handleMouseUp();
        }
      }}
    >
      {/* Grid Lines */}
      <div className="absolute inset-0 flex pointer-events-none">
        {timeIntervals.map((_, idx) => (
          <div
            key={idx}
            className="flex-shrink-0 border-r border-gray-50"
            style={{ width: intervalWidth }}
          />
        ))}
      </div>

      {/* Paint Preview Overlay */}
      {paintPreview && (
        <div
          className="absolute top-2 bottom-2 rounded border-2 border-dashed pointer-events-none z-10"
          style={{
            left: paintPreview.startPosition,
            width: paintPreview.width,
            backgroundColor: `${paintPreview.color}40`, // 40 = 25% opacity in hex
            borderColor: paintPreview.color,
          }}
        >
          <div className="flex items-center justify-center h-full text-xs font-medium text-white">
            {painterActivityType}
          </div>
        </div>
      )}

      {/* Schedule Blocks */}
      {scheduleBlocks.length === 0 && !paintPreview && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">
          No schedule
        </div>
      )}

      {/* Render schedule blocks */}
      {scheduleBlocks.map((block) => (
        <ScheduleBlock key={block.id} block={block} />
      ))}
    </div>
  );
}

// Schedule Block Component
interface ScheduleBlockProps {
  block: {
    id: string;
    activityType: string;
    channel?: string | null;
    startPosition: number;
    width: number;
    color: string;
    startTime: string;
    endTime: string;
    isAIGenerated: boolean;
    isOptimized: boolean;
  };
}

function ScheduleBlock({ block }: ScheduleBlockProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  // Make block draggable
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `block-${block.id}`,
    data: block,
  });

  // Determine if block needs visual warning (for demo - could be enhanced with real validation)
  const hasWarning = false; // TODO: Add real constraint checking

  // Build style object with conditional properties
  const blockStyle: React.CSSProperties = {
    left: block.startPosition,
    width: block.width,
    backgroundColor: block.color,
  };

  // Add conditional styling
  if (block.isOptimized) {
    blockStyle.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.6)'; // blue glow for AI-optimized
  }
  if (hasWarning) {
    blockStyle.boxShadow = '0 0 0 2px rgba(239, 68, 68, 0.6)'; // red glow for warnings
  }

  return (
    <motion.div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      layout
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: isDragging ? 0.5 : 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={`absolute top-2 bottom-2 rounded px-2 py-1 text-xs font-medium text-white shadow-md cursor-grab active:cursor-grabbing hover:shadow-lg transition-shadow ${
        isDragging ? '' : ''
      }`}
      style={blockStyle}
      onMouseEnter={() => !isDragging && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Block Content - Show full text if width allows */}
      <div className="flex items-center justify-between h-full">
        {/* Only show text if block is wide enough (at least 60px for reasonable readability) */}
        {block.width >= 60 && (
          <span className="truncate font-semibold text-xs">{block.activityType}</span>
        )}
        <div className="flex items-center space-x-0.5 ml-auto">
          {block.isAIGenerated && (
            <span className="text-xs">✨</span>
          )}
          {hasWarning && (
            <span className="text-xs">⚠️</span>
          )}
        </div>
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="absolute z-30 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-md shadow-lg whitespace-nowrap"
        >
          <div className="font-bold text-sm">{block.activityType}</div>
          {block.channel && (
            <div className="text-gray-300 mt-1">Channel: {block.channel}</div>
          )}
          <div className="text-gray-300 mt-1 font-medium">
            {block.startTime} - {block.endTime}
          </div>
          {block.isAIGenerated && (
            <div className="text-blue-300 mt-1 flex items-center">
              <span className="mr-1">✨</span>
              AI Generated
            </div>
          )}
          {block.isOptimized && (
            <div className="text-blue-300 mt-1 flex items-center">
              <span className="mr-1">🎯</span>
              Optimized by AI
            </div>
          )}
          {hasWarning && (
            <div className="text-yellow-300 mt-1 flex items-center">
              <span className="mr-1">⚠️</span>
              Has warnings
            </div>
          )}
          {/* Tooltip arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
        </motion.div>
      )}
    </motion.div>
  );
}

// Agent Detail Modal Component
interface AgentDetailModalProps {
  agent: any;
  onClose: () => void;
}

function AgentDetailModal({ agent, onClose }: AgentDetailModalProps) {
  const [preferences, setPreferences] = useState({
    workingHours: {
      start: '09:00',
      end: '17:00',
    },
    preferredChannels: [] as string[],
    breakPreferences: {
      preferredBreakTime: '12:00',
      mealDuration: 30,
      breakDuration: 15,
    },
    availability: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
    },
  });

  const handleSave = () => {
    // In a real app, this would call an API to save preferences
    console.log('Saving agent preferences:', { agentId: agent.id, preferences });
    onClose();
  };

  const toggleChannel = (channel: string) => {
    setPreferences({
      ...preferences,
      preferredChannels: preferences.preferredChannels.includes(channel)
        ? preferences.preferredChannels.filter((c) => c !== channel)
        : [...preferences.preferredChannels, channel],
    });
  };

  const toggleDay = (day: keyof typeof preferences.availability) => {
    setPreferences({
      ...preferences,
      availability: {
        ...preferences.availability,
        [day]: !preferences.availability[day],
      },
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-white text-lg font-medium">
              {agent.firstName[0]}{agent.lastName[0]}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {agent.firstName} {agent.lastName}
              </h2>
              <p className="text-sm text-gray-600">{agent.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Agent Info */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Agent Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Employee ID:</span>
                <span className="ml-2 font-medium text-gray-900">{agent.employeeId}</span>
              </div>
              <div>
                <span className="text-gray-600">Status:</span>
                <span className="ml-2 font-medium text-gray-900">{agent.status}</span>
              </div>
            </div>
          </div>

          {/* Working Hours */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Working Hours Preferences</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                <input
                  type="time"
                  value={preferences.workingHours.start}
                  onChange={(e) => setPreferences({
                    ...preferences,
                    workingHours: { ...preferences.workingHours, start: e.target.value },
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">End Time</label>
                <input
                  type="time"
                  value={preferences.workingHours.end}
                  onChange={(e) => setPreferences({
                    ...preferences,
                    workingHours: { ...preferences.workingHours, end: e.target.value },
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Preferred Channels */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Preferred Channels</h3>
            <div className="flex flex-wrap gap-2">
              {(['PHONE', 'CHAT', 'EMAIL'] as const).map((channel) => (
                <button
                  key={channel}
                  onClick={() => toggleChannel(channel)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    preferences.preferredChannels.includes(channel)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {channel}
                </button>
              ))}
            </div>
          </div>

          {/* Break Preferences */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Break Preferences</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Preferred Break Time</label>
                <input
                  type="time"
                  value={preferences.breakPreferences.preferredBreakTime}
                  onChange={(e) => setPreferences({
                    ...preferences,
                    breakPreferences: { ...preferences.breakPreferences, preferredBreakTime: e.target.value },
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Meal Duration (min)</label>
                  <input
                    type="number"
                    value={preferences.breakPreferences.mealDuration}
                    onChange={(e) => setPreferences({
                      ...preferences,
                      breakPreferences: { ...preferences.breakPreferences, mealDuration: parseInt(e.target.value) },
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Break Duration (min)</label>
                  <input
                    type="number"
                    value={preferences.breakPreferences.breakDuration}
                    onChange={(e) => setPreferences({
                      ...preferences,
                      breakPreferences: { ...preferences.breakPreferences, breakDuration: parseInt(e.target.value) },
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Availability */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Weekly Availability</h3>
            <div className="grid grid-cols-7 gap-2">
              {(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const).map((day) => (
                <button
                  key={day}
                  onClick={() => toggleDay(day)}
                  className={`px-2 py-3 rounded-md text-xs font-medium transition-colors ${
                    preferences.availability[day]
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {day.substring(0, 3).toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Skills */}
          {agent.skills && agent.skills.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Skills</h3>
              <div className="flex flex-wrap gap-2">
                {agent.skills.map((skill: any) => (
                  <span
                    key={skill.id}
                    className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium"
                  >
                    {skill.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}
